import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useBackTo } from '../../app/nav/originNav'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
import ConfirmDialog, { confirmDialog } from '../../components/ui/ConfirmDialog'
import EmptyState from '../../components/ui/EmptyState'
import PageHeader from '../../components/ui/PageHeader'
import Button from '../../components/ui/Button'
import { useToast } from '../../components/ui/ToastProvider'
import { useGrants } from '../../auth/useCurrentUser'
import { GRANTS, type GrantCode } from '../../auth/grants'
import { formatDate, formatTime } from '../../lib/datetime'
import type { EditOp } from '../../lib/editOp'
import DeferredSection from '../minutes/components/DeferredSection'
import ExcelImportSection from '../minutes/components/ExcelImportSection'
import ExcelImportPreviewDialog from '../minutes/components/ExcelImportPreviewDialog'
import { useSetBreadcrumb, type Crumb } from '../../app/nav/breadcrumb'
import { downloadMinutesReportPdf } from '../minutes/api/minutesApi'
import { useMeetingDetail } from './hooks/useMeetingDetail'
import { useMeetingContentSections } from './hooks/useMeetingContentSections'
import { useMeetingExcelImport } from './hooks/useMeetingExcelImport'
import { useSyncMeetingActions } from './hooks/useSyncMeetingActions'
import { usePublishMeeting } from './hooks/usePublishMeeting'
import MeetingEditCard from './components/MeetingEditCard'
import MeetingPeopleList from './components/MeetingPeopleList'
import MeetingDocumentsSection from './components/MeetingDocumentsSection'
import MeetingStatusBadge from './components/MeetingStatusBadge'
import MeetingHistoryDialog from './components/MeetingHistoryDialog'
import ActionsEditor from '../actions/components/ActionsEditor'
import ActionsView from '../actions/components/ActionsView'
import {
  ACTION_DECISION_TYPE_ID,
  actionToRow,
  buildActionPayload,
  type ActionFormRow,
} from '../actions/types'
import MinuteItemsEditor from '../minuteItems/MinuteItemsEditor'
import MinuteItemsView from '../minuteItems/MinuteItemsView'
import { KIND_BY_DECISION_TYPE } from '../minuteItems/types'
import { useMeetingRecordDelete } from './hooks/useMeetingRecordDelete'
import { useEditableMinuteItems } from './hooks/useEditableMinuteItems'
import { MEETING_STATUS, contentSectionTitle, type MeetingDetail } from './types'
import '../minutes/minutes.css'
import './meetings.css'

/**
 * Aksiyon satırlarının içerik imzası (yalnız kalıcı alanlar; her map'te değişen volatil `key` hariç). Toplantı
 * Detay aynı toplantı için yeniden fetch edildiğinde (ör. Aksiyon Detay'da statü değişip toplantıya dönülünce)
 * yerel satırların taze veriyle yeniden tohumlanıp tohumlanmayacağını belirler — yalnız `data.id`'ye bakan eski
 * koruma, aynı toplantının GÜNCELLENMİŞ verisini kaçırıp bayat statü gösteriyordu.
 */
function actionRowsSignature(rows: ActionFormRow[]): string {
  return JSON.stringify(
    rows.map((r) => [
      r.id,
      r.recordNumber,
      r.name,
      r.description,
      r.deadline,
      r.statusId,
      r.urgencyLevelId,
      r.importanceLevelId,
      // Sicil kümesi aynı kalıp YALNIZ rol değişebilir (ör. Aksiyon Detay'dan Ana Sorumlu'ya terfi).
      // Rol imzada olmazsa satırlar yeniden tohumlanmaz ve sonraki kayıt bayat rolleri geri yazarak
      // atanmış Ana Sorumlu'yu sessizce siler.
      r.assignees.map((p) => `${p.employeeId}:${p.assigneeTypeId ?? ''}`),
      // 120 — yıldız: takip AYRI uçtan değişir ve toplantı detayı tazelenir; imzada olmazsa satırlar
      // yeniden tohumlanmaz ve yıldız eski hâlinde kalırdı. Gövdeye girmez (buildActionPayload).
      r.followed ?? false,
      // 127 — Stratejik Hedef / Katkı Görev Detay'da AYRI değişir. İmzada olmazsa satır bayat seçimle
      // kalır ve sonraki toplu kayıt (replace-all) değişikliği sessizce GERİ YAZAR — rol ve yıldızla aynı
      // hata sınıfı (push kapısı review bulgusu, testle ölçüldü). Tanımsız ([null]) ile boş ([]) ayrı
      // tutulur: tanımsız "gönderme", boş "temizle" demektir.
      r.strategicObjectives?.map((o) => o.id) ?? null,
      r.contributions?.map((o) => o.id) ?? null,
    ]),
  )
}

/**
 * Toplantı (tutanak) Detay sayfası (044). <b>Temel Bilgiler</b> (ad/tarih/yer + katılımcılar + tutanaklar) kendi
 * <b>Düzenle</b> düğmesiyle aç-kapa düzenlenir (İptal/Kaydet o kart içinde). <b>Aksiyonlar</b> bölümü ise hep
 * düzenlenebilir gelir ve her ekle/düzenle/sil <b>anında</b> kaydedilir (bağımsız uç; Temel Bilgiler'den ayrı).
 * Gündem/Prensip/Karar görünür-pasif (tablo yok). İşlem Geçmişi sayfa başlığındaki eylemler arasında.
 * Arşivli/yetkisiz: her şey salt-okuma.
 */
/**
 * "Listeye Dön"ün veriden türetilen makul varsayılanı — origin yoksa (tam sayfa yenileme / derin link)
 * kullanılır: arşivli → Arşivlenen Toplantılar; taslak → Taslaklar (grup listesinde görünmez); değilse
 * geldiği grup toplantı listesi (grup yoksa Toplantılar).
 */
function fallbackListPath(archived: boolean, isDraft: boolean, meetingGroupId?: number | null) {
  if (archived) return '/minutes/archived/meetings'
  if (isDraft) return '/minutes/meeting-minutes/meetings/drafts'
  if (meetingGroupId != null) return `/minutes/meeting-minutes/meetings/group/${meetingGroupId}`
  return '/minutes/meeting-minutes/meetings'
}

/**
 * Header breadcrumb ek adımları: (Grup | Taslaklar →) Toplantı. Arşivlide ara adım verilmez; taslakta grup
 * drill-down'a link verilmez (taslak o listede görünmez) → "Taslaklar" adımı gösterilir.
 */
function breadcrumbSteps(
  data: MeetingDetail | undefined,
  archived: boolean,
  isDraft: boolean,
  draftsLabel: string,
): Crumb[] {
  if (!data) return []
  const self = { label: data.name }
  if (archived) return [self]
  if (isDraft) return [{ label: draftsLabel, to: '/minutes/meeting-minutes/meetings/drafts' }, self]
  if (data.groupName) {
    return [{ label: data.groupName, to: `/minutes/meeting-minutes/meetings/group/${data.meetingGroupId}` }, self]
  }
  return [self]
}

/**
 * Toplantı detayının <b>türetilmiş bayrakları</b>: durum (taslak/yayın/arşiv) + yetkiler.
 *
 * <p>Bileşenden ayrıldı (2026-09-15): {@code MeetingDetailPage} bilişsel karmaşıklık eşiğini aşmıştı
 * (SonarQube S3776, 17/15). Kuralların kendisi DEĞİŞMEDİ.
 *
 * <p><b>{@code canManageFiles} ile {@code canManage} bilinçli olarak AYRIDIR</b> — ikisi farklı yetkiye
 * bakar ({@code MEETING_FILE_MANAGE} vs {@code MINUTES_CREATE}); aynı adı taşımaları daha önce karışıklık
 * üretmişti (push kapısı code review bulgusu). Arşivli toplantıda dosya yönetimi kapalıdır, indirme açık.
 *
 * <p>{@code canDeleteRecord} (2026-09-11) düzenleme yetkisinden <b>bağımsızdır</b>: Genel Müdür tutanağı
 * düzenleyemez ama silebilir.
 */
function meetingPermissions(data: MeetingDetail | undefined, hasGrant: (g: GrantCode) => boolean) {
  const statusId = data?.meetingStatusId
  const archived = statusId === MEETING_STATUS.ARCHIVED
  const canManage = hasGrant(GRANTS.MINUTES_CREATE)
  return {
    canManage,
    canDeleteRecord: hasGrant(GRANTS.RECORD_DELETE),
    archived,
    canManageFiles: hasGrant(GRANTS.MEETING_FILE_MANAGE) && !archived,
    isDraft: statusId === MEETING_STATUS.DRAFT,
    isPublished: statusId === MEETING_STATUS.PUBLISHED,
    actionsEditable: canManage && !archived,
  }
}

export default function MeetingDetailPage() {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const { hasGrant } = useGrants()
  const [editing, setEditing] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [reporting, setReporting] = useState(false)
  // Katılımcılar tablosunun dar ekran karşılığı — bkz. ActionNoteCards deseni.
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const meetingId = id ? Number(id) : null
  const { data, isLoading, isError } = useMeetingDetail(meetingId)
  const {
    canManage,
    canDeleteRecord,
    archived,
    canManageFiles,
    isDraft,
    isPublished,
    actionsEditable,
  } = meetingPermissions(data, hasGrant)
  // "Listeye Dön": origin varsa (useNavigateWithOrigin ile taşınan GELDİĞİ liste) doğrudan oraya döner;
  // yoksa veriden türetilen varsayılan (bkz. fallbackListPath).
  const backTo = useBackTo(fallbackListPath(archived, isDraft, data?.meetingGroupId))
  const publish = usePublishMeeting(meetingId ?? 0)
  /**
   * Madde silme (gündem/prensip/karar — 2026-09-11). Yıkıcı işlem → onay istenir (Anayasa). Silinen
   * madde soft-delete'tir ve denetim izi yazılır; liste toplantı detayıyla birlikte tazelenir.
   */
  /**
   * Silme (görev + madde) — mantık kendi hook'unda; sayfa yalnız hazır iki geri çağrı alır. Koşul
   * `RECORD_DELETE` **ve** arşivli olmamak: silme düzenleme yetkisinden bağımsızdır (Genel Müdür
   * tutanağı düzenleyemez ama silebilir), arşivde ise sunucu da geçersiz durum döner.
   */
  const { onActionDelete, itemDeleteHandler } = useMeetingRecordDelete({
    meetingId: meetingId ?? 0,
    enabled: canDeleteRecord && !archived,
  })
  const confirmPublish = () =>
    confirmDialog({
      group: 'meeting-publish',
      header: t('meetings.detail.publish.confirmTitle'),
      message: t('meetings.detail.publish.confirmMessage'),
      icon: 'pi pi-send',
      acceptLabel: t('meetings.detail.publish.action'),
      accept: () =>
        publish.mutate(undefined, {
          onSuccess: () => {
            // Yayınla (360→361) sonrası donmuş origin'i (ör. Taslaklar listesi) temizle: aksi halde
            // "Listeye Dön" yayınlanan toplantının artık bulunmadığı Taslaklar'a götürür (boş liste).
            // State null'lanınca useBackTo veriden türetilen fallback'e (isDraft=false → grup listesi) düşer.
            navigate(location.pathname, { replace: true, state: null })
            toast.success(t('meetings.detail.publish.success'))
          },
          onError: () => toast.error(t('meetings.detail.publish.error')),
        }),
    })

  // Rapor Al: toplantının o anki güncel durumundan tutanak PDF'ini üretip indirir (kaydetmez).
  const onReport = async () => {
    if (meetingId == null) return
    setReporting(true)
    try {
      await downloadMinutesReportPdf(meetingId)
    } catch {
      toast.error(t('meetings.detail.report.error'))
    } finally {
      setReporting(false)
    }
  }

  const sections = useMeetingContentSections()

  // Aksiyonlar hep düzenlenebilir (editable modda) → yerel satır durumu; her değişiklik anında senkronlanır.
  const syncActions = useSyncMeetingActions(meetingId ?? 0)
  const [actionRows, setActionRows] = useState<ActionFormRow[]>([])
  const committedRef = useRef<ActionFormRow[]>([]) // son başarılı kaydedilen satırlar (hata → geri al)
  const initedRef = useRef<number | null>(null)
  // Sunucudan en son tohumlanan/senkronlanan aksiyon satırlarının içerik imzası. Aynı toplantı yeniden fetch
  // edilince (statü Aksiyon Detay'da değişip toplantıya dönülünce) satırları taze veriyle yeniden tohumlamak için.
  const actionsSigRef = useRef<string>('')
  // Her sync isteğine artan sıra no. Hızlı ekle/sil'de birden çok istek in-flight olur; yanıtlar SIRA DIŞI
  // gelebilir. Yalnız EN SON isteğin yanıtı uygulanır; geç gelen eski yanıt (silinen satırı yeniden getirir)
  // yok sayılır → silinen satır "ekranda kalıp refresh isteme" yarışı önlenir. Payload hep tüm satırları taşır.
  const syncSeqRef = useRef(0)

  // Basit içerikler (Gündem/Prensip/Karar) — aynı anında-kaydet deseni (bağımsız uçlar).
  const agenda = useEditableMinuteItems(meetingId ?? 0, 'agenda')
  const principles = useEditableMinuteItems(meetingId ?? 0, 'principles')
  const decisions = useEditableMinuteItems(meetingId ?? 0, 'decisions')
  const itemBundles = { agenda, principles, decisions } as const
  // init'ler useCallback ile stabil → effect deps'te bunlar listelenir (her render'da tetiklenmez).
  const { init: initAgenda } = agenda
  const { init: initPrinciples } = principles
  const { init: initDecisions } = decisions

  // Toplantı yüklenince (veya değişince) satırları detaydan başlat.
  useEffect(() => {
    if (!data) return
    // Gündem/Prensip/Karar: toplantı başına BİR kez tohumla (bu editörler başka ekrandan değişmez).
    if (initedRef.current !== data.id) {
      initAgenda(data.agenda)
      initPrinciples(data.principles)
      initDecisions(data.decisions)
      initedRef.current = data.id
    }
    // Aksiyonlar: sunucudan gelen liste son tohumlanandan farklıysa yeniden tohumla — statü/alanlar Aksiyon
    // Detay'da değişip toplantıya dönülünce (refetch) güncel görünsün. Sync mutasyonu detay cache'ini YAZMADIĞI
    // için bu, satır-içi düzenleme sırasında churn yapmaz; yalnız gerçek bir refetch verisinde tetiklenir. Devam
    // eden iyimser düzenleme committedRef'ten sapmışsa imza eşit kalır (sunucu = commit) → clobberlamaz.
    const rows = data.actions.map(actionToRow)
    const sig = actionRowsSignature(rows)
    if (sig !== actionsSigRef.current) {
      setActionRows(rows)
      committedRef.current = rows
      actionsSigRef.current = sig
    }
    // init'ler useCallback ile stabil → yalnız data değişince çalışır (her render'da değil).
  }, [data, initAgenda, initPrinciples, initDecisions])

  /**
   * **Excel ile Toplu Yükleme** (088'in ikinci kullanım yeri; kullanıcı isteği 2026-08-19).
   *
   * Doğrulama ucu Tutanak Oluştur ile **aynıdır** (durumsuz, kayıt yazmaz); farklı olan kalıcılaşma
   * yolu: burada satırlar bölümlerin **kendi anında-kaydet uçlarına** gider. Aktarım mevcut maddelerin
   * **sonuna ekler** (kullanıcı kararı) — "değiştir" elle girilmiş maddeleri silmek olurdu.
   *
   * `handleActionsChange`/`bundle.onChange` iyimser güncelleme + sunucu senkronunu zaten yönetiyor;
   * bu yüzden ekleme onların üzerinden yapılır ve burada ikinci bir kaydetme yolu doğmaz.
   */
  const excel = useMeetingExcelImport({
    meetingId: data?.id,
    meetingDate: data?.meetingDate.slice(0, 10),
    sectionIds: (sections.data ?? []).map((s) => s.id),
    onAppendActions: (rows) => handleActionsChange([...actionRows, ...rows], 'add'),
    onAppendItems: (kind, rows) => {
      const bundle = itemBundles[kind]
      bundle.onChange([...bundle.rows, ...rows], 'add')
    },
  })

  const handleActionsChange = (rows: ActionFormRow[], op: EditOp) => {
    setActionRows(rows) // iyimser
    const payload = buildActionPayload(rows)
    if (payload === 'invalid') {
      setActionRows(committedRef.current)
      toast.error(t('actions.editor.invalid'))
      return
    }
    const mySeq = ++syncSeqRef.current
    syncActions.mutate(payload, {
      onSuccess: (updated) => {
        // Bu istek en son gönderilen değilse yanıtı yut: daha yeni bir düzenleme zaten uçmuş; eski/bayat
        // sunucu durumunu uygulamak silinen satırı geri getirir (yarış). En son isteğin yanıtı tek gerçek.
        if (mySeq !== syncSeqRef.current) return
        const fresh = updated.map(actionToRow)
        committedRef.current = fresh
        setActionRows(fresh)
        // İmzayı senkron sonucu sunucu gerçeğiyle hizala → sonraki refetch aynı veriyi getirince gereksiz
        // yeniden-tohumlama olmasın (yalnız veri GERÇEKTEN değişince yeniden tohumlanır).
        actionsSigRef.current = actionRowsSignature(fresh)
        toast.success(
          t(`meetings.detail.saved.${op}`, { item: t('meetings.detail.saved.item.action') }),
        )
      },
      onError: () => {
        if (mySeq !== syncSeqRef.current) return // daha yeni istek var → onun sonucu belirleyici
        setActionRows(committedRef.current) // sunucu gerçeğine geri dön
        toast.error(t('meetings.detail.actionsSaveError'))
      },
    })
  }

  useSetBreadcrumb(breadcrumbSteps(data, archived, isDraft, t('meetings.drafts.title')))

  if (isError) {
    return (
      <div className="page minutes-page">
        <PageHeader title={t('meetings.detail.pageTitle')} />
        <EmptyState
          icon="pi pi-exclamation-triangle"
          title={t('meetings.detail.notFound.title')}
          description={t('meetings.detail.notFound.desc')}
        />
      </div>
    )
  }

  return (
    <div className="page minutes-page">
      <PageHeader
        title={data?.name ?? t('meetings.detail.pageTitle')}
        actions={
          <>
            <Button
              variant="secondary"
              icon="pi pi-arrow-left"
              label={t('meetings.detail.back')}
              onClick={() => navigate(backTo)}
            />
            {isPublished && hasGrant(GRANTS.MEETING_REPORT_VIEW) && (
              <Button
                variant="secondary"
                icon="pi pi-file-pdf"
                label={t('meetings.detail.report.action')}
                onClick={onReport}
                disabled={reporting}
              />
            )}
            {/* İşlem Geçmişi — 075: MEETING_HISTORY_VIEW yetkisi; düzenleme modunda gizli. Yeri sayfanın
                EN ALTIYDI, kullanıcı isteğiyle (2026-08-28) diğer sayfa eylemlerinin arasına alındı:
                salt-okuma eylemler (Rapor · İşlem Geçmişi) yan yana, birincil eylem (Yayınla) en sonda. */}
            {hasGrant(GRANTS.MEETING_HISTORY_VIEW) && !editing && (
              <Button
                variant="secondary"
                icon="pi pi-history"
                label={t('meetings.detail.history')}
                onClick={() => setHistoryOpen(true)}
              />
            )}
            {isDraft && hasGrant(GRANTS.MINUTES_PUBLISH) && (
              <Button
                icon="pi pi-send"
                label={t('meetings.detail.publish.action')}
                onClick={confirmPublish}
                disabled={publish.isPending}
              />
            )}
          </>
        }
      />
      <ConfirmDialog group="meeting-publish" />
      {/* Madde ve görev silme onayları (gruba özel — Anayasa: yıkıcı işlem). */}
      <ConfirmDialog group="item-delete" />
      <ConfirmDialog group="meeting-action-delete" />

      <div className="mtg-detail">
        {editing && data ? (
          /* Temel Bilgiler düzenleme (ad/tarih/yer + katılımcılar + tutanaklar). Kaydet/İptal kart içinde. */
          <MeetingEditCard
            detail={data}
            onCancel={() => setEditing(false)}
            onSaved={() => setEditing(false)}
          />
        ) : (
          /* Temel Bilgiler kartı (salt-okuma) — sağ üstte Düzenle (yalnız yetkili + arşivli değil). */
          <section className="minutes-section">
            <div className="minutes-section-head">
              <h2>{t('meetings.detail.info.title')}</h2>
              {data && canManage && !archived && (
                <Button
                  className="mtg-edit-toggle"
                  variant="secondary"
                  size="small"
                  icon="pi pi-pencil"
                  label={t('meetings.detail.edit.edit')}
                  onClick={() => setEditing(true)}
                />
              )}
            </div>
            <dl className="mtg-info-grid">
              <div>
                <dt>{t('meetings.detail.columns.number')}</dt>
                <dd>{data?.recordNumber ?? ''}</dd>
              </div>
              <div>
                <dt>{t('meetings.detail.info.name')}</dt>
                <dd>{data?.name ?? ''}</dd>
              </div>
              <div>
                <dt>{t('meetings.detail.info.group')}</dt>
                <dd>{data?.groupName ?? ''}</dd>
              </div>
              <div>
                <dt>{t('meetings.detail.info.date')}</dt>
                <dd>{data ? formatDate(data.meetingDate) : ''}</dd>
              </div>
              <div>
                <dt>{t('meetings.detail.info.time')}</dt>
                <dd>
                  {data
                    ? `${formatTime(data.meetingDate)} - ${formatTime(data.meetingEndDate)}`
                    : ''}
                </dd>
              </div>
              <div>
                <dt>{t('meetings.detail.info.location')}</dt>
                <dd>{data?.meetingLocation ?? ''}</dd>
              </div>
              <div>
                <dt>{t('meetings.status.label')}</dt>
                <dd>
                  <MeetingStatusBadge statusId={data?.meetingStatusId ?? null} />
                </dd>
              </div>
              <div>
                <dt>{t('meetings.detail.info.enteredUser')}</dt>
                <dd>{data?.enteredUserName ?? ''}</dd>
              </div>
            </dl>

            {/* Katılımcılar ve hemen altında dağıtım listesi (119) — salt-okuma, aynı görünüm. */}
            <MeetingPeopleList
              title={t('meetings.detail.participants.title')}
              people={data?.participants ?? []}
              emptyTitle={t('meetings.detail.participants.emptyTitle')}
              emptyDesc={t('meetings.detail.participants.emptyDesc')}
              isLoading={isLoading}
              isMobile={isMobile}
            />
            <MeetingPeopleList
              title={t('meetings.detail.distributionList.title')}
              people={data?.distributionList ?? []}
              emptyTitle={t('meetings.detail.distributionList.emptyTitle')}
              emptyDesc={t('meetings.detail.distributionList.emptyDesc')}
              isLoading={isLoading}
              isMobile={isMobile}
            />
          </section>
        )}

        {/* İçerik listeleri — HER MODDA görünür. Düzenlenebilirse editör (anında kaydet), değilse salt-okuma.
            Aksiyon: karmaşık editör; Gündem/Prensip/Karar: basit içerik editörü; tanımsız tip: görünür-pasif. */}
        {(sections.data ?? []).map((s) => {
          const title = contentSectionTitle(s, i18n.language)
          const kind = KIND_BY_DECISION_TYPE[s.id]
          if (s.id === ACTION_DECISION_TYPE_ID) {
            return actionsEditable ? (
              <ActionsEditor
                key={s.id}
                title={title}
                value={actionRows}
                onChange={handleActionsChange}
                meetingDate={data?.meetingDate.slice(0, 10)}
                onDelete={onActionDelete}
              />
            ) : (
              <ActionsView
                key={s.id}
                title={title}
                actions={data?.actions ?? []}
                onDelete={onActionDelete}
              />
            )
          }
          if (kind) {
            const bundle = itemBundles[kind]
            return actionsEditable ? (
              <MinuteItemsEditor
                key={s.id}
                kind={kind}
                title={title}
                value={bundle.rows}
                onChange={bundle.onChange}
                // Sürükle-bırak yalnız Gündem'de: yeni sıra record_number'a (1..N) yazılır → "No" değişir.
                onReorder={kind === 'agenda' ? bundle.reorder : undefined}
              />
            ) : (
              <MinuteItemsView
                key={s.id}
                kind={kind}
                title={title}
                items={data?.[kind] ?? []}
                onDelete={itemDeleteHandler(kind)}
              />
            )
          }
          return (
            <DeferredSection
              key={s.id}
              title={title}
              description={t('meetings.detail.content.desc')}
            />
          )
        })}

        {/* Excel ile toplu yükleme — yalnız düzenlenebilir modda (arşivde/yetkisizde hiç render edilmez).
            Yeri Tutanak Oluştur ile BİREBİR aynı (kullanıcı isteği 2026-08-19): içerik listelerinin
            ALTINDA, Tutanaklar bölümünün üstünde. İki ekranın aynı işi aynı yerde sunması şart —
            kullanıcı bölümü ekran değiştirdiğinde yeniden aramamalı. */}
        {actionsEditable && (
          <ExcelImportSection
            downloading={excel.templateDownloading}
            uploading={excel.uploading}
            onDownloadTemplate={excel.onDownloadTemplate}
            onFileSelected={excel.onFileSelected}
          />
        )}

        {/* Tutanaklar — yeri sabit; HER MODDA ve her yetkide AYNI bileşen/tasarım (tablo). Yetki farkı
            yalnız eylemleri gizler: `canManage` false iken yükleme alanı ve Kaldır ikonu çıkmaz.
            Önce burada yetkiye göre iki ayrı bileşene (tablo / `AttachmentList` kart listesi)
            dallanılıyordu; kullanıcı isteği 2026-08-25: aynı veri için iki tasarım kullanılmaz. */}
        <MeetingDocumentsSection
          meetingId={meetingId ?? 0}
          documents={data?.documents ?? []}
          canManage={canManageFiles}
        />
      </div>

      {/* Ön izleme pop-up'ı — `append` modunda: kaç satırın eklenaceğini yazar (çiftleme riski onaylanır). */}
      <ExcelImportPreviewDialog
        visible={excel.preview != null}
        preview={excel.preview}
        mode="append"
        appendableRowCount={excel.appendableRowCount}
        skippedRowCount={excel.skippedRowCount}
        // `append` modunda anlamı yok (replace uyarısı yalnız Tutanak Oluştur'da çıkar).
        hasExistingExcelRows={false}
        onCancel={excel.onCancel}
        onTransfer={excel.onTransfer}
      />

      {hasGrant(GRANTS.MEETING_HISTORY_VIEW) && meetingId != null && (
        <MeetingHistoryDialog
          meetingId={meetingId}
          visible={historyOpen}
          onHide={() => setHistoryOpen(false)}
        />
      )}
    </div>
  )
}
